import type { VehicleAgent } from "../types";

interface SimulationSceneProps {
  vehicles: VehicleAgent[];
}

const stateStyle: Record<VehicleAgent["currentState"], string> = {
  idle: "bg-slate-600 text-white",
  driving: "bg-sky-600 text-white",
  waiting_for_charge: "bg-amber-500 text-white",
  charging: "bg-emerald-600 text-white",
};

const VehicleChip = ({ vehicle }: { vehicle: VehicleAgent }) => (
  <div
    className={`rounded-md px-2 py-1 text-[11px] font-semibold shadow-sm ${stateStyle[vehicle.currentState]}`}
    title={`${vehicle.id} • ${vehicle.currentState}`}
  >
    {vehicle.id}
  </div>
);

export default function SimulationScene({ vehicles }: SimulationSceneProps) {
  const stationAIdle = vehicles.filter(
    (vehicle) => vehicle.position.zone === "station" && vehicle.position.stationId === "stationA",
  );
  const stationBIdle = vehicles.filter(
    (vehicle) => vehicle.position.zone === "station" && vehicle.position.stationId === "stationB",
  );
  const stationAQueue = vehicles.filter(
    (vehicle) => vehicle.position.zone === "queue" && vehicle.position.stationId === "stationA",
  );
  const stationBQueue = vehicles.filter(
    (vehicle) => vehicle.position.zone === "queue" && vehicle.position.stationId === "stationB",
  );
  const stationACharging = vehicles.filter(
    (vehicle) => vehicle.position.zone === "charger" && vehicle.position.stationId === "stationA",
  );
  const stationBCharging = vehicles.filter(
    (vehicle) => vehicle.position.zone === "charger" && vehicle.position.stationId === "stationB",
  );
  const drivingVehicles = vehicles.filter((vehicle) => vehicle.position.zone === "road");

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <h4 className="text-sm font-semibold text-slate-700">Station A Area</h4>
          <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-500">Idle</p>
          <div className="mt-1 flex flex-wrap gap-1.5">{stationAIdle.map((vehicle) => <VehicleChip key={vehicle.id} vehicle={vehicle} />)}</div>
          <p className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">Queue</p>
          <div className="mt-1 flex flex-wrap gap-1.5">{stationAQueue.map((vehicle) => <VehicleChip key={vehicle.id} vehicle={vehicle} />)}</div>
          <p className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">Chargers</p>
          <div className="mt-1 flex flex-wrap gap-1.5">{stationACharging.map((vehicle) => <VehicleChip key={vehicle.id} vehicle={vehicle} />)}</div>
        </div>

        <div className="rounded-xl border border-dashed border-sky-300 bg-sky-50 p-3">
          <h4 className="text-sm font-semibold text-sky-700">Road / Driving</h4>
          <p className="mt-2 text-[11px] uppercase tracking-wide text-sky-500">In Transit</p>
          <div className="mt-1 flex min-h-[140px] flex-wrap content-start gap-1.5">
            {drivingVehicles.length === 0 ? (
              <span className="text-xs text-sky-600">No vehicles on road</span>
            ) : (
              drivingVehicles.map((vehicle) => <VehicleChip key={vehicle.id} vehicle={vehicle} />)
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <h4 className="text-sm font-semibold text-slate-700">Station B Area</h4>
          <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-500">Idle</p>
          <div className="mt-1 flex flex-wrap gap-1.5">{stationBIdle.map((vehicle) => <VehicleChip key={vehicle.id} vehicle={vehicle} />)}</div>
          <p className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">Queue</p>
          <div className="mt-1 flex flex-wrap gap-1.5">{stationBQueue.map((vehicle) => <VehicleChip key={vehicle.id} vehicle={vehicle} />)}</div>
          <p className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">Chargers</p>
          <div className="mt-1 flex flex-wrap gap-1.5">{stationBCharging.map((vehicle) => <VehicleChip key={vehicle.id} vehicle={vehicle} />)}</div>
        </div>
      </div>
    </div>
  );
}
